import { Localized, useLocalization } from "@fluent/react";
import {
    Alert,
    AppBar,
    Badge,
    Button,
    ButtonGroup,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Switch,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { Skeleton } from '@mui/material';
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useCallback, useEffect, useMemo, useState } from "react";
import { If } from "../../app/If";
import { AppId, AppTextFieldHandler } from "../../app/appTypes";
import { showError, showSuccess } from "../notifications/notificationsSlice";
import { Box } from "@mui/system";
import produce from "immer";
import { selectSettings } from '../settings/settingsSlice';
import MenuItem from '@mui/material/MenuItem';
import { ReqCriticalityLabel } from "../urs/ReqCriticalityLabel";
import { LoadingButton, TabContext, TabList, TabPanel } from "@mui/lab";
import { Check, Clear } from "@mui/icons-material";
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { NoteLabel } from "../ursOffer/NoteLabel";
import { useLocation } from "react-router-dom";
import { Blobs, loadManyBlobs, Uuid } from "../documents/attachementsSlice";
import { BlobLink } from "../documents/BlobLink";
import DownloadIcon from '@mui/icons-material/Download';
import { ExternalRequirementNote, ExternalUrsOffer, ExternalUrsOfferRequirement, addNote, finishCommenting, loadExternalUrsOffer, rejectExternalOffer, selectAllExternalOfferRequirements, sendExternalToEvaluation, postExternalUrsRequirementComment, ExternalRequirementComment, deleteExternalUrsRequirementComment, updateExternalUrsRequirementComment, allExternalOfferRequirements, validateExternalOffer } from "./externalOfferSlice";
import { Comment, CommentKind, DiscussOld } from "../../app/Discuss";
import { parseISODate } from "../../app/Utils";
import { ExternalOfferHeader } from "./ExternalOfferHeader";
import PrintIcon from '@mui/icons-material/Print';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckIcon from '@mui/icons-material/Check';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { SaveButton } from "../../app/components/SaveButton";
import { CancelButton } from "../../app/components/CancelButton";
import { DocumentNumberBox } from "../../app/components/DocumentNumberBox";
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ExpiredLinkPanel from "../../app/ExpiredLinkPanel";
import ResignationConfirmationPanel from "./pages/ResignationConfirmationPanel";
import CommentSubmittedPanel from "./pages/RegisteredCommentSentPanel";
import UnregisteredCommentSentPanel from "./pages/UnregisteredCommentSentPanel";
import { LanguageSelector } from "../../LanguageSelector";
import { RegistrationOptionBox } from "./pages/RegistrationOptionBox";
import { IncompleteOfferDialog } from "./pages/IncompleteOfferDialog";
import { ImportedExternalUrsContent } from "./ImportedExternalUrsContent";
import CommentIcon from '@mui/icons-material/Comment';

type MainTabs = "reqs" | "description" | "attachments"

const LoadComponent = () => <Skeleton variant="rectangular" height="50vh" />

export const ExternalOfferForm = () => {
    const { search } = useLocation()

    const params = new URLSearchParams(search)

    const token = params.has("t") ? params.get("t") : "";
    const mode = "external"

    const dispatch = useAppDispatch()

    const [urs, setUrs] = useState<undefined | "error" | ExternalUrsOffer>(undefined)
    const [blobs, setBlobs] = useState<Blobs[]>([])
    const [blobsError, setBlobsError] = useState(false)

    function* allURSRequirements(urs: ExternalUrsOffer) {
        for (const type of urs.types) {
            for (const req of type.requirements) {
                yield req
            }
            for (const group of type.groups) {
                for (const req of group.requirements) {
                    yield req
                }
            }
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            let entity: undefined | ExternalUrsOffer = undefined
            if (urs === undefined && token) {
                try {
                    entity = await dispatch(loadExternalUrsOffer(token)).unwrap()
                    setUrs(entity)
                } catch (err) {
                    if (err !== null && typeof err === 'object' && err['name'] && err['name'] === 'ConditionError') {
                        // dispatch cancelled
                        console.log(err)
                    } else {
                        setUrs("error")
                    }
                }
                try {
                    if (entity) {
                        const allRequirements = Array.from(allURSRequirements(entity))
                        const blobs = await dispatch(loadManyBlobs([entity.guid, ...allRequirements.map(x => x.guid)])).unwrap()
                        setBlobs(blobs)
                    }
                } catch (error) {
                    setBlobsError(true)
                }
            }
        }
        fetchData()
    }, [dispatch, urs, token])

    switch (urs) {
        case undefined:
            return <LoadComponent />
        case "error":
            return <ExpiredLinkPanel />
    }

    return <>
        {blobsError && <Alert severity="error"><Localized id='loading-blobs-error'>Wystąpił problem z załadowaniem załączników</Localized></Alert>}
        <Form entity={urs} blobs={blobs} mode={mode} token={token ?? ""} />
    </>
}

interface URSRequirementRowProps {
    requirement: ExternalUrsOfferRequirement
    comments: ExternalRequirementComment[]
    onApproveClick: (req: ExternalUrsOfferRequirement) => Promise<void>
    onEditClick: (req: ExternalUrsOfferRequirement) => void
    canEdit: boolean
}

const URSRequirementRow = ({ requirement, comments, onEditClick, onApproveClick, canEdit }: URSRequirementRowProps) => {
    const [approving, setApproving] = useState(false)
    const commentsCount = useMemo(() => {
        return comments.filter(c => c.ursRequirementId.toString() === requirement.id).length
    }, [comments, requirement])

    const handleApproveClick = useCallback(async () => {
        setApproving(true)
        try {
            await onApproveClick(requirement)
        } catch (error) {
            console.log(error)
        } finally {
            setApproving(false)
        }
    }, [])

    return <TableRow hover>
        <TableCell>
            <DocumentNumberBox value={requirement.numeration}></DocumentNumberBox>
        </TableCell>
        <TableCell>
            <div dangerouslySetInnerHTML={{ __html: requirement?.name ?? "" }}></div>
        </TableCell>
        <TableCell>{<ReqCriticalityLabel criticality={requirement.criticality ?? ""} />}</TableCell>
        <TableCell sx={{ textAlign: "center" }}>
            <NoteLabel note={requirement.note} />
        </TableCell>
        <TableCell>{requirement.comment}</TableCell>
        <TableCell sx={{ textAlign: "center" }}>
            <ButtonGroup variant="outlined" color="secondary" size="small">

                <If condition={canEdit}>
                    <Tooltip title={<Localized id="urs-offer-action-quick-approve"><span>_quick approve</span></Localized>}>
                        <IconButton disabled={approving} size="small" onClick={handleApproveClick}
                            sx={{
                                color: 'default',
                                '&:hover': {
                                    color: 'success.main',
                                },
                            }}>
                            <DoneAllIcon />
                        </IconButton>
                    </Tooltip>
                    <Button size="small" onClick={() => onEditClick(requirement)}>
                        <Localized id="urs-offer-action-rate">
                            <span>Oceń</span>
                        </Localized>
                    </Button>
                    {commentsCount > 0 &&
                        <Badge color='secondary' badgeContent={commentsCount} invisible={commentsCount === 0}>
                            <Tooltip title={<Localized id="discussion"><span>Dyskusja</span></Localized>}>
                                <IconButton size="small" onClick={() => onEditClick(requirement)}>
                                    <CommentIcon />
                                </IconButton>
                            </Tooltip>
                        </Badge>
                    }
                </If>

            </ButtonGroup>
        </TableCell>
    </TableRow>
}

type DialogState = "none" | "addReqComment" | "confirmSendOffer" | "confirmRejectOffer" | "confirmSendToEvaluate" | "offerDetail" | "selectDevice" | "confirmAutoMatch"

export const Form = ({ entity, mode, blobs, token }: { token: string, entity: ExternalUrsOffer, mode: string, blobs: Blobs[] }) => {
    const dispatch = useAppDispatch()
    const { l10n } = useLocalization()
    const [draftEntity, setDraftEntity] = useState(entity)
    const { locale } = useAppSelector(selectSettings);

    const [tab, setTab] = useState<MainTabs>("reqs")
    const [dialog, setDialog] = useState<DialogState>("none")
    const [saving, setSaving] = useState(false)
    const [comments, setComments] = useState<ExternalRequirementComment[]>(entity.comments)
    const [fullScreen, setFullScreen] = useState(false);
    const toggleFullScreen = () => setFullScreen((prev) => !prev);
    const handleReqCommentChange: AppTextFieldHandler = useCallback(e => {
        setReqComment(e.target.value)
        setAnyChange(true)
    }, [])
    const handleReqCommentStatusChange: AppTextFieldHandler = useCallback(e => {
        setReqNote(e.target.value as ExternalRequirementNote)
        setAnyChange(true)
    }, [])

    const [requirement, setRequirement] = useState<ExternalUrsOfferRequirement | null>(null)
    const [reqNote, setReqNote] = useState<ExternalRequirementNote>("")
    const [reqComment, setReqComment] = useState("")
    const [anyChange, setAnyChange] = useState(false)
    const [offerComment, setOfferComment] = useState("")
    const [registrationType, setRegistrationType] = useState('withregistration');

    const [email, setEmail] = useState("");
    const [emailConfirmation, setEmailConfirmation] = useState("");
    const [emailError, setEmailError] = useState("");

    const [city, setCity] = useState("");
    const [country, setCountry] = useState("");
    const [zipcode, setZipcode] = useState("");
    const [state, setState] = useState("");
    const [companyName, setCompanyName] = useState(draftEntity.supplierName);
    const [address, setAddress] = useState("");
    const [phone, setPhone] = useState("");
    const [surname, setSurname] = useState("");
    const [firstName, setFirstName] = useState("");

    const [firstNameError, setFirstNameError] = useState(false);
    const [surnameError, setSurnameError] = useState(false);
    const [companyNameError, setCompanyNameError] = useState(false);
    const [incompleteDialogOpen, setIncompleteDialogOpen] = useState(false);
    const [showOnlyNonEvaluated, setShowOnlyNonEvaluated] = useState(false)

    const handleEmailChange: AppTextFieldHandler = useCallback(e => setEmail(e.target.value), [setEmail])
    const handleEmailConfrimationChange: AppTextFieldHandler = useCallback(e => setEmailConfirmation(e.target.value), [setEmailConfirmation])
    const handleFirstNameChange: AppTextFieldHandler = useCallback(e => setFirstName(e.target.value), [setFirstName])
    const handleSurnameChange: AppTextFieldHandler = useCallback(e => setSurname(e.target.value), [setSurname])
    const handleCompanyNameChange: AppTextFieldHandler = useCallback(e => setCompanyName(e.target.value), [setCompanyName])
    const handlePhoneChange: AppTextFieldHandler = useCallback(e => setPhone(e.target.value), [setPhone])
    const handleAddressChange: AppTextFieldHandler = useCallback(e => setAddress(e.target.value), [setAddress])
    const handleCityChange: AppTextFieldHandler = useCallback(e => setCity(e.target.value), [setCity])
    const handleStateChange: AppTextFieldHandler = useCallback(e => setState(e.target.value), [setState])
    const handleZipCodeChange: AppTextFieldHandler = useCallback(e => setZipcode(e.target.value), [setZipcode])
    const handleCountryChange: AppTextFieldHandler = useCallback(e => setCountry(e.target.value), [setCountry])

    const imported: boolean = entity.metadata !== null

    const goNext = useCallback((req: ExternalUrsOfferRequirement) => {
        const gen = allExternalOfferRequirements(draftEntity)
        let result = gen.next()
        while (result.value !== undefined && result.value.id !== req.id && result.done === false) {
            result = gen.next()
        }
        if (result.done === false) {
            result = gen.next()
            if (result.value) {
                setRequirement(result.value)
                setReqNote(result.value.note)
                setReqComment(result.value.comment)
            }
        }
    }, [draftEntity])

    const clearErrors = () => {
        setEmailError("")
        setFirstNameError(false);
        setSurnameError(false);
        setCompanyNameError(false);
    }
    const handleRadioChange = (event) => {
        setRegistrationType(event.target.value);
    };
    const requirementComments = useMemo(() => {
        return comments.filter(({ ursRequirementId }) => ursRequirementId.toString() === requirement?.id).map(({ id, message, author, parentMessageId, userId, date }) => {
            return {
                someId: id,
                content: message,
                author: author ?? "Bez nazwy",
                parentId: parentMessageId,
                authorId: userId,
                date: parseISODate(date),
                companyName: companyName
            } as Comment
        })
    }, [requirement, comments])

    const postComment = async (message: string, parentCommentId: AppId | null, kind: CommentKind): Promise<Uuid | undefined> => {
        if (requirement !== null) {
            try {
                const newComment = await dispatch(postExternalUrsRequirementComment({
                    token,
                    comment: {
                        id: "",
                        ursOfferId: entity.id,
                        ursRequirementId: requirement.id,
                        message,
                        author: "",
                        userId: null,
                        date: new Date().toISOString(),
                        parentMessageId: parentCommentId,
                        subMessages: [],
                        status: "",
                        guid: '',
                        type: kind,
                    },
                })).unwrap()
                setComments(produce(draft => {
                    draft.push(newComment)
                }))
                return newComment.guid
            } catch (e) {
                /* handle error */
                console.log(e)
            }
        }
    }
    const updateComment = async (commentId: AppId, message: string) => {
        const comment = comments.find(x => x.id === commentId)
        if (requirement !== null && comment) {
            try {
                const newComment = await dispatch(updateExternalUrsRequirementComment({
                    token,
                    comment: {
                        ...comment,
                        message,
                    },
                })).unwrap()
                setComments(produce(draft => {
                    const i = draft.findIndex(x => x.id === commentId)
                    if (i >= 0) {
                        draft[i] = newComment
                    }
                }))
            } catch (e) {
                /* handle error */
                console.log(e)
            }
        }
    }
    const deleteComment = async (id: AppId) => {
        try {
            await dispatch(deleteExternalUrsRequirementComment({
                token,
                commentId: id,
            })).unwrap()
            setComments(oldComments => oldComments.filter(comment => comment.id !== id))
        } catch (e) {
            /* handle error */
            console.log(e)
        }
    }

    const [contextTypeId, setContextTypeId] =
        useState<AppId | undefined>(entity.types.length > 0 ? entity.types[0].id : undefined)
    const contextType = useMemo(() => draftEntity.types.find(x => x.id === contextTypeId), [contextTypeId, draftEntity.types])

    const handleReqsTabChange = useCallback((_, newType) => {
        setContextTypeId(newType)
    }, [])

    const handleMainTabChange = useCallback((_, newTab) => {
        setTab(newTab)
    }, [])

    const save = async () => {
        setSaving(true)
        if (requirement) {
            try {
                const newOffer = await dispatch(addNote({
                    token: token,
                    requirementId: requirement.id,
                    note: reqNote,
                    comment: reqComment
                })).unwrap()
                setAnyChange(false)

                setDraftEntity(
                    produce(draft => {
                        for (const mainGr of draft.types) {
                            for (const req of mainGr.requirements) {
                                if (req.id === requirement.id) {
                                    req.note = reqNote
                                    req.comment = reqComment
                                }
                                if (req.childRequirements) {
                                    for (const childReq of req.childRequirements) {
                                        if (childReq.id === requirement.id) {
                                            childReq.note = reqNote
                                            childReq.comment = reqComment
                                        }
                                    }
                                }
                            }
                            for (const group of mainGr.groups) {
                                for (const req of group.requirements) {
                                    if (req.id === requirement.id) {
                                        req.note = reqNote
                                        req.comment = reqComment

                                    }
                                    if (req.childRequirements) {
                                        for (const childReq of req.childRequirements) {
                                            if (childReq.id === requirement.id) {
                                                childReq.note = reqNote
                                                childReq.comment = reqComment
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }))
                dispatch(showSuccess("saved"));

            } catch (error) {
                dispatch(showError("error"));
            } finally {
                setSaving(false)
            }
        }

    }

    const handleCommentOKButtonClickk = async () => {
        await save()
        setDialog("none")
    }

    const handleSaveAndGoNextClick = async () => {
        if (anyChange) {
            await save()
        }
        requirement && goNext(requirement)
    }

    const handleQuickApproveButtonClick = useCallback(async (updatedRequirement: ExternalUrsOfferRequirement) => {
        setSaving(true)
        if (updatedRequirement) {
            try {
                const result = await dispatch(addNote({
                    token: token,
                    requirementId: updatedRequirement.id,
                    note: 'F',
                    comment: updatedRequirement.comment
                })).unwrap()

                setDraftEntity(
                    produce(draft => {
                        for (const mainGr of draft.types) {
                            for (const req of mainGr.requirements) {
                                if (req.id === updatedRequirement.id) {
                                    req.note = 'F'
                                }
                                if (req.childRequirements) {
                                    for (const childReq of req.childRequirements) {
                                        if (childReq.id === updatedRequirement.id) {
                                            childReq.note = 'F'
                                        }
                                    }
                                }
                            }
                            for (const group of mainGr.groups) {
                                for (const req of group.requirements) {
                                    if (req.id === updatedRequirement.id) {
                                        req.note = 'F'
                                    }
                                    if (req.childRequirements) {
                                        for (const childReq of req.childRequirements) {
                                            if (childReq.id === updatedRequirement.id) {
                                                childReq.note = 'F'
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }))
                dispatch(showSuccess("saved"));


            } catch (error) {
                dispatch(showError("error"));
            }

        }

        setDialog("none")
        setSaving(false)

    }, [])


    const handleDialogCancelClick = useCallback(() => {
        setDialog("none")
        setAnyChange(false)
    }, [])

    const handlePrintUrsOfferClick = async () => {
        if (draftEntity.id) {
            window.open(`${process.env.REACT_APP_REST_API}api/reports/urs-offer/${draftEntity.id}?language=${locale}`, '_blank')
        }
    }

    const handleFinishOfferClick = async () => {
        try {
            const isValid = await dispatch(validateExternalOffer(token)).unwrap();

            if (isValid) {
                setDialog("confirmSendOffer");
            } else {
                setIncompleteDialogOpen(true);

            }
        } catch (error) {
            dispatch(showError("error"))
        }
    };

    const handleRejectOfferClick = async () => {
        setOfferComment("")
        setDialog("confirmRejectOffer")
    }

    const handleSendToEvaluateClick = async () => {
        setOfferComment("")
        setDialog("confirmSendToEvaluate")
    }

    const handleOfferCommentChange: AppTextFieldHandler = useCallback(e => setOfferComment(e.target.value), [])



    const handleConfirmSendOfferClick = async () => {
        try {
            clearErrors()
            if (!Validate()) {

                return;
            }
            setSaving(true)
            const result = await dispatch(finishCommenting({
                token: token,
                email: email,
                emailConfirmation: emailConfirmation,
                option: registrationType,
                firstName: firstName,
                surname: surname,
                phone: phone,
                address: address,
                companyName: companyName,
                city: city,
                state: state,
                zipcode: zipcode,
                country: country
            }));

            if (finishCommenting.fulfilled.match(result)) {
                const updatedEntity = result.payload as ExternalUrsOffer;
                setDraftEntity(updatedEntity)
                dispatch(showSuccess("saved"))
            }
            else {
                let errorMsg = "error"
                if (result.payload) {
                    if (result.payload.kind === 'http') {
                        const problem = result.payload.problem
                        if (problem) {
                            errorMsg = problem.title
                        }
                    }
                }
                dispatch(showError(errorMsg))
            }
        }
        catch (error) {
            dispatch(showError("error"))
        }
        setDialog("none")
        setSaving(false)
    }


    const handleConfirmRejectOfferClick = async () => {
        try {
            setSaving(true)
            const updatedEntity = await dispatch(rejectExternalOffer({ token: token, comment: offerComment })).unwrap()
            setDraftEntity(updatedEntity)
            dispatch(showSuccess("saved"))
        } catch (error) {
            dispatch(showError("error"))
        }
        setDialog("none")
        setSaving(false)
    }


    const handleConfirmSendToEvaluateClick = async () => {
        try {
            setSaving(true)
            const updatedEntity = await dispatch(sendExternalToEvaluation({ token: token, comment: offerComment })).unwrap()
            setDraftEntity(updatedEntity)
            dispatch(showSuccess("saved"))
        } catch (error) {
            dispatch(showError("error"))
        }
        setDialog("none")
        setSaving(false)
    }


    const handleEditRequirementClick = useCallback((requirement: ExternalUrsOfferRequirement) => {

        const { note, comment } = requirement
        setRequirement(requirement)
        setReqNote(note)
        setReqComment(comment)
        setDialog("addReqComment")

    }, [])

    const handleShowOnlyNonevaluatedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setShowOnlyNonEvaluated(event.target.checked);
    }

    const reqFilter = useCallback((r: ExternalUrsOfferRequirement) => {
        if (showOnlyNonEvaluated) {
            return r.note === ""
        } else {
            return true
        }
    }, [showOnlyNonEvaluated])

    function Validate() {
        let valResult = true;
        validateEmail()

        if (firstName === '') {
            valResult = false;
            setFirstNameError(true);
        }

        if (surname === '') {
            valResult = false;
            setSurnameError(true);
        }

        if (companyName === '') {
            valResult = false;
            setCompanyNameError(true);
        }

        if (emailError != '') {
            valResult = false;

        }
        return valResult;
    }
    const validateEmail = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        let valResult = true;

        if (!emailRegex.test(email)) {
            setEmailError(l10n.getString("email-incorrect", null, "_Nieprawidłowy adres e-mail"));
            valResult = false;
        }


        else if (email !== '' && emailConfirmation !== email) {
            setEmailError(l10n.getString("emails-do-not-match", null, "_emails not match"));
            valResult = false;
        }
        else {
            setEmailError("");
        }
        return valResult;
    };

    const editable = (draftEntity.status === "N" || draftEntity.status === "E") && mode !== "preview"

    if (contextTypeId === undefined || contextType === undefined) return <div>Nothing to display</div>

    if (draftEntity.status === "R")
        return <ResignationConfirmationPanel />

    if (draftEntity.status === "A" && registrationType == 'withregistration')
        return <CommentSubmittedPanel />

    if (draftEntity.status === "A" && registrationType == 'noregistration')
        return <UnregisteredCommentSentPanel />

    return (
        <Container sx={{
            paddingTop: 2,
            paddingBottom: 2,
        }} component={Paper} maxWidth={false} >
            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <ExternalOfferHeader offer={draftEntity}></ExternalOfferHeader>
                </Grid>
                <Grid item xs={12}>
                    <TabContext value={tab}>
                        <TabList onChange={handleMainTabChange}>
                            <Tab
                                key={0}
                                value="reqs"
                                label={<Localized id="urs-requirements">Lista wymagań</Localized>} />
                            <Tab
                                key={1}
                                value="description"
                                label={<Localized id="urs-purpose-and-description">Cel i opis</Localized>} />
                            <Tab
                                key={2}
                                value="attachments"
                                label={<Localized id="urs-attachements">Załączniki</Localized>} />
                        </TabList>
                        <TabPanel value="reqs">
                            {imported ? <ImportedExternalUrsContent urs={entity} token={token} /> : <Box sx={{
                                display: 'flex',
                                flexGrow: 1,
                            }}>
                                <Tabs
                                    orientation="vertical"
                                    variant="standard"
                                    value={contextTypeId}
                                    onChange={handleReqsTabChange}
                                    sx={{
                                        borderRight: 1,
                                        borderColor: 'divider',
                                    }}
                                >
                                    {Array.from(draftEntity.types, t => <Tab
                                        key={t.id}
                                        label={t.code}
                                        value={t.id}
                                    />)}
                                </Tabs>
                                <Stack sx={{
                                    paddingLeft: 4,
                                    width: '100%',
                                }} direction="column" spacing={2}>
                                    <Typography variant="subtitle2">
                                        <If condition={contextTypeId !== undefined}
                                            otherwise={<></>}
                                        >{`${contextType.numeration} ${contextType.code} ${contextType.name}`}</If>
                                    </Typography>
                                    <TableContainer sx={{
                                        maxHeight: 'calc(100vh - 400px)',
                                        overflowY: 'auto',
                                    }} component={Paper}>
                                        <Table stickyHeader >
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell sx={{ minWidth: 120, width: 120, maxWidth: 120 }}>
                                                        <Typography component="span" variant="subtitle2" color="textSecondary">
                                                            <Localized id="urs-item-lp">Lp</Localized>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Typography component="span" variant="subtitle2" color="textSecondary">
                                                            <Localized id="urs-item-name">Nazwa</Localized>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 120, width: 120, maxWidth: 120 }}>
                                                        <Typography component="span" variant="subtitle2" color="textSecondary">
                                                            <Localized id="urs-item-criticality">Krytyczność</Localized>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 100, width: 100, maxWidth: 100, textAlign: "center" }}>
                                                        <Typography component="span" variant="subtitle2" color="textSecondary">
                                                            <Localized id="urs-offer-note">Ocena</Localized>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 200 }}>
                                                        <Typography component="span" variant="subtitle2" color="textSecondary">
                                                            <Localized id="urs-offer-comment">Komentarz</Localized>
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 100, width: 100, maxWidth: 100, justifyContent: 'flex-end' }}></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {contextType.requirements.filter(reqFilter).map(requirement =>
                                                    <>
                                                        <URSRequirementRow
                                                            key={requirement.id}
                                                            comments={comments}
                                                            requirement={requirement}
                                                            onEditClick={handleEditRequirementClick}
                                                            canEdit={editable}
                                                            onApproveClick={handleQuickApproveButtonClick}
                                                        />
                                                        {
                                                            requirement.childRequirements != null && requirement.childRequirements.filter(reqFilter).map(childReq =>
                                                                <URSRequirementRow
                                                                    key={childReq.id}
                                                                    comments={comments}
                                                                    requirement={requirement}
                                                                    onEditClick={handleEditRequirementClick}
                                                                    canEdit={editable}
                                                                    onApproveClick={handleQuickApproveButtonClick}
                                                                />
                                                            )
                                                        }
                                                    </>
                                                )
                                                }
                                                {contextType.groups.map(group =>
                                                    <>
                                                        <TableRow sx={{ bgcolor: '#f1f1f1' }}>
                                                            <TableCell colSpan={5}>{`${group.numeration} ${group.name}`}</TableCell>
                                                        </TableRow>
                                                        {group.requirements.filter(reqFilter).map(requirement =>

                                                            <>
                                                                <URSRequirementRow
                                                                    key={requirement.id}
                                                                    comments={comments}
                                                                    requirement={requirement}
                                                                    onEditClick={handleEditRequirementClick}
                                                                    onApproveClick={handleQuickApproveButtonClick}
                                                                    canEdit={editable} />
                                                                {requirement.childRequirements != null && requirement.childRequirements.filter(reqFilter).map(childReq =>
                                                                    <URSRequirementRow
                                                                        key={childReq.id}
                                                                        comments={comments}
                                                                        requirement={requirement}
                                                                        onEditClick={handleEditRequirementClick}
                                                                        onApproveClick={handleQuickApproveButtonClick}
                                                                        canEdit={editable}
                                                                    />
                                                                )}
                                                            </>
                                                        )
                                                        }
                                                    </>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Stack>
                            </Box>}
                        </TabPanel>
                        <TabPanel value="description">
                            <Stack direction="column" spacing={4} >
                                <Box>
                                    <Typography variant="h5" component="h5" gutterBottom>
                                        <Localized id="urs-title"><span>Tytuł dokumentu</span></Localized>
                                    </Typography>
                                    <div>{entity.title}</div>
                                </Box>
                                <Box>
                                    <Typography variant="h5" component="h5" gutterBottom>
                                        <Localized id="urs-purpose"><span>Cel</span></Localized>
                                    </Typography>
                                    <div dangerouslySetInnerHTML={{ __html: entity.purpose }} />
                                </Box>
                                <Box>
                                    <Typography variant="h5" component="h5" gutterBottom>
                                        <Localized id="urs-description"><span>Opis</span></Localized>
                                    </Typography>
                                    <div dangerouslySetInnerHTML={{ __html: entity.description }} />
                                </Box>
                                <Box>
                                    <Typography variant="h5" component="h5" gutterBottom>
                                        <Localized id="urs-area"><span>Obszar działania</span></Localized>
                                    </Typography>
                                    <div dangerouslySetInnerHTML={{ __html: entity.area }} />
                                </Box>
                            </Stack>
                        </TabPanel>
                        <TabPanel value="attachments">
                            <AttachmentsTable blobs={blobs} entity={entity} />
                        </TabPanel>
                    </TabContext>
                </Grid>

            </Grid>
            <AppBar position="fixed" color="transparent" sx={{ top: 'auto', bottom: 0, p: 0.5 }}>
                <div className="flex justify-end">
                    <Box display="flex" alignItems="center" pl={1} gap={2}>
                        <LanguageSelector />
                    </Box>
                    <Box sx={{ flexGrow: .25, display: 'flex', justifyContent: 'center' }}>
                        <FormGroup row>
                            <FormControlLabel control={
                                <Switch checked={showOnlyNonEvaluated}
                                    onChange={handleShowOnlyNonevaluatedChange} />}
                                label={<Localized id="urs-offer-show-only-not-evaluated">Pokaż tylko nieocenione</Localized>}
                            />
                        </FormGroup>
                    </Box>
                    <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
                        <Stack spacing={2} direction="row">
                            <Button
                                onClick={() => handleFinishOfferClick()}
                                variant="contained"
                                color="success"
                                size="small"
                                startIcon={<CheckIcon />}
                            >
                                <Localized id="urs-offer-action-send-offer">Złóż ofertę</Localized>
                            </Button>
                            <Button
                                onClick={() => handleRejectOfferClick()}
                                variant="contained"
                                color="error"
                                size="small"
                                startIcon={<CancelIcon />}
                            >
                                <Localized id="urs-offer-action-reject">Zrezygnuj</Localized>
                            </Button>
                        </Stack>
                    </Box>
                    <Box component="span" display="flex" alignItems="center" gap={1}>
                        <Button
                            onClick={() => handlePrintUrsOfferClick()}
                            variant="contained"
                            size="small"
                            startIcon={<PrintIcon />}
                        >
                            <Localized id="urs-offer-action-print">Wydruk</Localized>
                        </Button>

                    </Box>

                </div>


            </AppBar>

            <Dialog open={dialog === "addReqComment"}
                fullWidth
                maxWidth={fullScreen ? undefined : 'md'}
                fullScreen={fullScreen}>
                <Box
                    sx={{
                        m: fullScreen ? 4 : 0,
                        borderRadius: fullScreen ? 2 : 0,
                        border: fullScreen ? '1px solid #ccc' : 'none',
                        boxShadow: fullScreen ? 6 : 'none',
                        backgroundColor: 'background.paper',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <DialogTitle sx={{ backgroundColor: '#f5f5f5' }}>
                        <Localized id="urs-offer-evaluation-title">
                            <span>Wprowadź ocenę wymagania</span>
                        </Localized>
                        <IconButton onClick={toggleFullScreen} style={{ float: 'right' }} color="primary">
                            {fullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers>
                        <Grid container spacing={0}>
                            {/* Lewa strona */}
                            <Grid item xs={12} md={6} sx={{ pr: { md: 2, xs: 0 } }}>
                                <Box
                                    p={2}
                                    sx={{
                                        backgroundColor: '#fafafa',
                                        borderRadius: 1,
                                        border: '1px solid #eee',
                                        height: '100%',
                                        flexDirection: 'column',
                                        maxHeight: '60vh',

                                        overflow: 'auto',
                                    }}
                                >
                                    <DocumentNumberBox value={requirement?.numeration ?? "-"} />
                                    <Typography
                                        variant="body1"
                                        color="text.primary"
                                        dangerouslySetInnerHTML={{ __html: requirement?.name ?? '' }}
                                        sx={{ mt: 1 }}
                                    />
                                </Box>
                            </Grid>

                            {/* Prawa strona */}
                            <Grid item xs={12} md={6}>
                                <Box
                                    p={1.5}
                                    mb={2}
                                    sx={{
                                        backgroundColor: '#fafafa',
                                        borderRadius: 1,
                                        border: '1px solid #eee',
                                    }}
                                >
                                    <Typography variant="subtitle2">
                                        <Localized id="urs-offer-supplier-note">
                                            <span>_ocen dostawcy</span>
                                        </Localized>
                                    </Typography>
                                </Box>

                                <Box display="flex" flexDirection="column" gap={2}>
                                    <TextField
                                        value={reqNote}
                                        select
                                        label={
                                            <Localized id="urs-offer-req-opinion">
                                                <span>Ocena</span>
                                            </Localized>
                                        }
                                        onChange={handleReqCommentStatusChange}
                                        size="small"
                                        sx={{ maxWidth: 300 }}
                                        fullWidth
                                    >
                                        <MenuItem value={"F"}>
                                            <Check fontSize="small" color="success" />
                                            <Localized id="urs-offer-req-f"><span> Spełnia</span></Localized>
                                        </MenuItem>
                                        <MenuItem value={"U"}>
                                            <Clear fontSize="small" color="error" />
                                            <Localized id="urs-offer-req-u"><span> Nie spełnia</span></Localized>
                                        </MenuItem>
                                        <MenuItem value={"D"}>
                                            <QuestionMarkIcon fontSize="small" color="warning" />
                                            <Localized id="urs-offer-req-d"><span> Do oceny</span></Localized>
                                        </MenuItem>
                                    </TextField>

                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={4}
                                        maxRows={6}
                                        inputProps={{ maxLength: 1000 }}
                                        value={reqComment}
                                        onChange={handleReqCommentChange}
                                        label={
                                            <Localized id="urs-offer-req-comment">
                                                <span>Komentarz</span>
                                            </Localized>
                                        }
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12}>
                                <Box mt={2} mb={1} >
                                    <DiscussOld
                                        postComment={postComment}
                                        deleteComment={deleteComment}
                                        updateComment={updateComment}
                                        allComments={requirementComments}
                                        currentUserId={null}
                                        readOnly={false}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    </DialogContent>

                    <DialogActions>
                        <SaveButton loading={saving} onClick={handleCommentOKButtonClickk} />
                        <SaveButton loading={saving} onClick={handleSaveAndGoNextClick} label={<Localized id='save-and-next'>Zapisz & Następny</Localized>} />
                        <CancelButton loading={saving} onClick={handleDialogCancelClick} />
                    </DialogActions>
                </Box>
            </Dialog>

            <Dialog open={dialog === "confirmSendToEvaluate"}>
                <DialogTitle sx={{ backgroundColor: '#f5f5f5' }}>
                    <Localized id="urs-offer-action-send-to-evaluation-question">
                        <span>Czy wysłać oferte do oceny klienta?</span>
                    </Localized>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Alert severity="warning" sx={{ marginTop: 2 }}>
                            <Localized id="urs-offer-action-send-to-evaluation-question-descripton">
                                <span>Komentowanie nadal możliwe po wysyłce?</span>
                            </Localized>
                        </Alert>
                    </DialogContentText>
                    <TextField
                        multiline
                        rows={5}
                        onChange={handleOfferCommentChange}
                        value={offerComment}
                        InputLabelProps={{
                            shrink: true
                        }}
                        variant="outlined"
                        fullWidth={true}
                        sx={{ marginTop: "20px", minWidth: "400px" }}
                        label={<Localized id="urs-offer-evaluate-comment"><span>Komentarz</span></Localized>}
                    />
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={saving} onClick={handleConfirmSendToEvaluateClick}>
                        <Localized id="send"><span>Wyślij</span></Localized>
                    </LoadingButton>
                    <Button onClick={handleDialogCancelClick}>
                        <Localized id="cancel"><span>Anuluj</span></Localized>
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog maxWidth="md" fullWidth open={dialog === "confirmSendOffer"}>
                <DialogTitle sx={{ backgroundColor: '#f5f5f5' }}>
                    <Localized id="urs-offer-action-send-offer-question">
                        <span>Czy napewno zakończyć komentowanie</span>
                    </Localized>
                </DialogTitle>

                <DialogContent>
                    <Container sx={{ mt: 1, px: 1 }}>
                        <Grid container spacing={2} >
                            <Grid item xs={12} md={6} >
                                <RegistrationOptionBox
                                    type="withregistration"
                                    selected={registrationType === 'withregistration'}
                                    onSelect={() => handleRadioChange({ target: { value: 'withregistration' } })}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <RegistrationOptionBox
                                    type="noregistration"
                                    selected={registrationType === 'noregistration'}
                                    onSelect={() => handleRadioChange({ target: { value: 'noregistration' } })}
                                />
                            </Grid>
                        </Grid>


                        <Grid container spacing={2} mt={1}>
                            <Box
                                sx={{
                                    border: '1px solid #ddd',
                                    borderRadius: 2,
                                    overflow: 'hidden'
                                }}
                            >
                                <Box
                                    sx={{
                                        backgroundColor: '#f5f5f5',
                                        px: 2,
                                        py: 1.5,
                                        borderBottom: '1px solid #ddd',
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                        <Localized id="data-required-to-finish-process">_Dane wymagane</Localized>
                                    </Typography>
                                </Box>

                                <Box sx={{ p: 2 }}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                value={email}
                                                fullWidth
                                                required
                                                onBlur={validateEmail}
                                                error={!!emailError}
                                                helperText={emailError}
                                                type="email"
                                                onChange={handleEmailChange}
                                                inputProps={{ maxLength: 100 }}
                                                label={<Localized id="user-email">Email</Localized>}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField
                                                value={emailConfirmation}
                                                required
                                                fullWidth
                                                onBlur={validateEmail}
                                                error={!!emailError}
                                                helperText={emailError}
                                                type="email"
                                                onChange={handleEmailConfrimationChange}
                                                inputProps={{ maxLength: 100 }}
                                                label={<Localized id="user-email-confirm">Potwierdź</Localized>}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                required
                                                fullWidth
                                                inputProps={{ maxLength: 50 }}
                                                value={firstName}
                                                error={firstNameError}
                                                onChange={handleFirstNameChange}
                                                label={<Localized id="user-first-name">Imię</Localized>}
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField
                                                required
                                                fullWidth
                                                value={surname}
                                                error={surnameError}
                                                inputProps={{ maxLength: 100 }}
                                                onChange={handleSurnameChange}
                                                label={<Localized id="user-last-name">Nazwisko</Localized>}
                                                size="small"
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Box>
                            {registrationType === 'withregistration' && (
                                <Box
                                    sx={{
                                        border: '1px solid #ddd',
                                        borderRadius: 2,
                                        overflow: 'hidden',
                                        marginTop: 1
                                    }}
                                >
                                    <Box
                                        sx={{
                                            backgroundColor: '#f5f5f5',
                                            px: 2,
                                            py: 1.5,
                                            borderBottom: '1px solid #ddd',
                                        }}
                                    >
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                            <Localized id="data-optional-to-finish-process">_Dane opcjonalne</Localized>
                                        </Typography>
                                    </Box>

                                    <Box sx={{ p: 2 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    value={companyName}
                                                    onChange={handleCompanyNameChange}
                                                    error={companyNameError}
                                                    label={<Localized id="companies-name">Nazwa firmy</Localized>}
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    value={phone}
                                                    inputProps={{ maxLength: 20 }}
                                                    onChange={handlePhoneChange}
                                                    label={<Localized id="companies-phone">Telefon</Localized>}
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    value={address}
                                                    onChange={handleAddressChange}
                                                    label={<Localized id="companies-address">Adres</Localized>}
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    value={city}
                                                    onChange={handleCityChange}
                                                    label={<Localized id="companies-address-city">Miasto</Localized>}
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    value={state}
                                                    onChange={handleStateChange}
                                                    label={<Localized id="companies-address-state">Stan</Localized>}
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    value={zipcode}
                                                    onChange={handleZipCodeChange}
                                                    label={<Localized id="companies-address-zip-code">Kod pocztowy</Localized>}
                                                    size="small"
                                                />
                                            </Grid>
                                            <Grid item xs={6}>
                                                <TextField
                                                    fullWidth
                                                    value={country}
                                                    onChange={handleCountryChange}
                                                    label={<Localized id="companies-address-country">Kraj</Localized>}
                                                    size="small"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Box>
                            )}
                        </Grid>
                    </Container>
                </DialogContent>
                <DialogActions>
                    <SaveButton loading={saving} onClick={handleConfirmSendOfferClick} />
                    <CancelButton loading={saving} onClick={handleDialogCancelClick} />
                </DialogActions>
            </Dialog>

            <Dialog open={dialog === "confirmRejectOffer"}>
                <DialogTitle sx={{ backgroundColor: '#f5f5f5' }}>
                    <Localized id="urs-offer-action-resign-question">
                        <span>Czy napewno zrezygnować ze składania oferty?</span>
                    </Localized>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Alert severity="warning" sx={{ marginTop: 2 }}>
                            <Localized id="urs-offer-action-resign-question-description">
                                <span>Komentowanie nie bedzie juz mozliwe</span>
                            </Localized>
                        </Alert>
                    </DialogContentText>
                    <TextField
                        multiline
                        fullWidth={true}
                        rows={5}
                        onChange={handleOfferCommentChange}
                        value={offerComment}
                        variant="outlined"
                        InputLabelProps={{
                            shrink: true
                        }}
                        sx={{ marginTop: "20px", minWidth: "400px" }}
                        label={<Localized id="urs-offer-resign-comment"><span>Komentarz</span></Localized>}
                    />
                </DialogContent>
                <DialogActions>
                    <LoadingButton
                        loading={saving}
                        size="small"
                        color="error"
                        variant="contained"
                        startIcon={<CancelIcon />}
                        onClick={handleConfirmRejectOfferClick}
                    >
                        <Localized id="urs-offer-action-reject"><span>Zrezygnuj</span></Localized>
                    </LoadingButton>
                    <CancelButton loading={saving} onClick={handleDialogCancelClick} />
                </DialogActions>
            </Dialog>
            <IncompleteOfferDialog open={incompleteDialogOpen} onClose={() => setIncompleteDialogOpen(false)} />

        </Container >
    );
}

const AttachmentsTable = ({ blobs, entity }: { blobs: Blobs[], entity: ExternalUrsOffer }) => {
    const [heads, ...items] = blobs
    const headBlobs = heads.blobs ?? []
    const allRequirements = useMemo(() => Array.from(allExternalOfferRequirements(entity)), [entity])
    return <TableContainer>
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell sx={{ width: '50%' }}></TableCell>
                    <TableCell></TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {headBlobs.map(blob => {
                    return <TableRow key={blob.blobId}>
                        <TableCell colSpan={2}>
                            <BlobLink blobId={blob.blobId} target="_blank">
                                <IconButton><DownloadIcon /></IconButton>
                            </BlobLink>&nbsp;{blob.fileMeta.fileName}
                        </TableCell>
                    </TableRow>
                })}
                {items.map(({ resourceId, blobs }) => {
                    if (blobs === null || blobs.length === 0) {
                        return null
                    } else {
                        const requirement = allRequirements.find(x => x.guid === resourceId)
                        const [blob0, ...rest] = blobs
                        return [blob0].map(blob => {
                            return <TableRow key={blob.blobId}>
                                <TableCell>
                                    <BlobLink blobId={blob.blobId} target="_blank">
                                        <IconButton><DownloadIcon /></IconButton>
                                    </BlobLink>&nbsp;{blob.fileMeta.fileName}
                                </TableCell>
                                <TableCell rowSpan={blobs.length}>{requirement?.name ?? ""}</TableCell>
                            </TableRow>
                        }).concat(rest.map(blob => {
                            return <TableRow key={blob.blobId}>
                                <TableCell>
                                    <BlobLink blobId={blob.blobId} target="_blank">
                                        <IconButton><DownloadIcon /></IconButton>
                                    </BlobLink>&nbsp;{blob.fileMeta.fileName}
                                </TableCell>
                            </TableRow>
                        }))
                    }
                })}
            </TableBody>
        </Table>
    </TableContainer>
}

